import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 0 }

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { MediaVideoProps } from '@/types/components/MediaVideo';
import AdaptiveMedia from '@/elements/AdaptiveMedia.vue';
import utils from '@/utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'MediaVideo',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-media-video'));

const rootStyle = computed(() => {
  return utils.overall.baseComStyle(props.fields);
});

const sectionEl = ref<HTMLElement | null>(null);
const maskLeftEl = ref<HTMLElement | null>(null);
const maskRightEl = ref<HTMLElement | null>(null);
const contentLeftEl = ref<HTMLElement | null>(null);
const contentRightEl = ref<HTMLElement | null>(null);

let triggers: ScrollTrigger[] = [];

const titleField = computed(() => props.fields?.title);
const descField = computed(() => props.fields?.description);
const mediaField = computed(() => props.fields?.media);

const buildAnimation = () => {
  if (
    !sectionEl.value ||
    !maskLeftEl.value ||
    !maskRightEl.value ||
    !contentLeftEl.value ||
    !contentRightEl.value
  ) {
    return;
  }

  triggers.forEach((t) => t.kill());
  triggers = [];

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sectionEl.value,
      start: 'top top',
      end: '+=100%',
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      scrub: 1,
    },
  });

  // 左遮罩：平行四边形向左移出，右边缘斜率 -0.2 (60%→40%)
  tl.fromTo(
    maskLeftEl.value,
    { clipPath: 'polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%)' },
    { clipPath: 'polygon(-50% 0%, 0% 0%, 0% 100%, -50% 100%)', ease: 'none' },
    0
  );

  // 右遮罩：与左遮罩边缘微量重叠，避免 clip-path 抗锯齿产生白缝
  tl.fromTo(
    maskRightEl.value,
    { clipPath: 'polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%)' },
    { clipPath: 'polygon(100% 0%, 150% 0%, 150% 100%, 100% 100%)', ease: 'none' },
    0
  );

  // 文字同步裁切
  tl.fromTo(
    contentLeftEl.value,
    { clipPath: 'polygon(0% 0%, 60% 0%, 40% 100%, 0% 100%)' },
    { clipPath: 'polygon(-50% 0%, 0% 0%, 0% 100%, -50% 100%)', ease: 'none' },
    0
  );
  tl.fromTo(
    contentRightEl.value,
    { clipPath: 'polygon(59.95% 0%, 100% 0%, 100% 100%, 39.95% 100%)' },
    { clipPath: 'polygon(100% 0%, 150% 0%, 150% 100%, 100% 100%)', ease: 'none' },
    0
  );

  if (tl.scrollTrigger) {
    triggers.push(tl.scrollTrigger);
  }
};

onMounted(() => {
  buildAnimation();
  window.addEventListener('resize', buildAnimation);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', buildAnimation);
  triggers.forEach((t) => t.kill());
  triggers = [];
});

return (_ctx: any,_cache: any) => {
  return (__props.fields)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value),
        ref_key: "sectionEl",
        ref: sectionEl,
        id: __props.rendering?.dataSource,
        "data-no-reveal": ""
      }, [
        (mediaField.value)
          ? (_openBlock(), _createBlock(AdaptiveMedia, {
              key: 0,
              class: "c-media-video__video",
              field: mediaField.value,
              autoplay: true,
              loop: true,
              options: { preload: 'auto' },
              "show-image-redius": false,
              "is-background": ""
            }, null, 8, ["field"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: "c-media-video__mask c-media-video__mask--left",
          ref_key: "maskLeftEl",
          ref: maskLeftEl
        }, null, 512),
        _createElementVNode("div", {
          class: "c-media-video__mask c-media-video__mask--right",
          ref_key: "maskRightEl",
          ref: maskRightEl
        }, null, 512),
        _createElementVNode("div", {
          class: "c-media-video__content c-media-video__content--left",
          ref_key: "contentLeftEl",
          ref: contentLeftEl
        }, [
          (titleField.value)
            ? (_openBlock(), _createElementBlock("h2", _hoisted_2, [
                _createVNode(_unref(RichText), {
                  class: "c-media-video__title",
                  field: titleField.value
                }, null, 8, ["field"])
              ]))
            : _createCommentVNode("", true),
          _cache[0] || (_cache[0] = _createElementVNode("div", { class: "c-media-video__divider" }, null, -1)),
          (descField.value)
            ? (_openBlock(), _createBlock(_unref(RichText), {
                key: 1,
                class: "c-media-video__desc",
                field: descField.value
              }, null, 8, ["field"]))
            : _createCommentVNode("", true)
        ], 512),
        _createElementVNode("div", {
          class: "c-media-video__content c-media-video__content--right",
          ref_key: "contentRightEl",
          ref: contentRightEl
        }, [
          (titleField.value)
            ? (_openBlock(), _createElementBlock("h2", _hoisted_3, [
                _createVNode(_unref(RichText), {
                  class: "c-media-video__title",
                  field: titleField.value
                }, null, 8, ["field"])
              ]))
            : _createCommentVNode("", true),
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "c-media-video__divider" }, null, -1)),
          (descField.value)
            ? (_openBlock(), _createBlock(_unref(RichText), {
                key: 1,
                class: "c-media-video__desc",
                field: descField.value
              }, null, 8, ["field"]))
            : _createCommentVNode("", true)
        ], 512)
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})